import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Form, InputGroup, Button, Modal, Spinner, Alert } from "react-bootstrap";
import { collection, getDocs, updateDoc, doc, getDoc } from "firebase/firestore";
import { db, callXeroFunction } from "../firebase.js";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";
import { format as fnsFormat, parseISO } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import Lightbox from "../components/common/Lightbox.js";

export default function DocumentsPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [searchClient, setSearchClient] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [clients, setClients] = useState([]);

  // Email states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ clientEmail: "", clientName: "", customMessage: "" });
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [sending, setSending] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState("");
  const [uploadProgress, setUploadProgress] = useState({ stage: "", percent: 0 });

  // Client autocomplete states
  const [postgresClients, setPostgresClients] = useState([]);
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Lightbox state
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Load PostgreSQL clients for autocomplete
  useEffect(() => {
    const loadPostgresClients = async () => {
      try {
        const response = await callXeroFunction('getClients', {});
        setPostgresClients(response.clients || []);
      } catch (error) {
        console.error('Erro ao carregar clientes PostgreSQL:', error);
      }
    };

    if (user?.isAdmin) {
      loadPostgresClients();
    }
  }, [user]);

  // Filter client suggestions based on input
  const handleClientNameChange = (value) => {
    setEmailData({ ...emailData, clientName: value });

    if (value.trim().length >= 2) {
      const filtered = postgresClients.filter(client =>
        client.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 10); // Limit to 10 suggestions
      setClientSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setClientSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Select client from suggestions
  const handleSelectClient = (client) => {
    setEmailData({
      ...emailData,
      clientName: client.name,
      clientEmail: client.email || ''
    });
    setShowSuggestions(false);
    setClientSuggestions([]);
  };

  // Funcao unificada para carregar documentos (usada no useEffect e no reload)
  const fetchAllDocuments = async () => {
    try {
      const allDocs = [];

      // Buscar clientes para resolver clientId para nome
      const clientsRef = collection(db, "clients");
      const clientsSnapshot = await getDocs(clientsRef);
      const clientsMap = {};
      clientsSnapshot.forEach((doc) => {
        clientsMap[doc.id] = doc.data().name;
      });
      setClients(clientsMap);

      // Buscar todos os relatórios semanais
      const reportsRef = collection(db, "weeklyReports");
      const reportsSnapshot = await getDocs(reportsRef);

      reportsSnapshot.forEach((doc) => {
        const data = doc.data();
        const { uid, slug, weekStart, days } = data;

        // Para cada dia da semana
        Object.keys(days || {}).forEach((dayName) => {
          const entries = days[dayName] || [];

          // Para cada entrada do dia
          entries.forEach((entry, index) => {
            // Só adicionar se tiver arquivos
            if (entry.uploadedFiles && entry.uploadedFiles.length > 0) {
              allDocs.push({
                id: `${doc.id}_${dayName}_${index}`,
                reportId: doc.id,
                uid,
                slug,
                weekStart,
                dayName,
                index,
                client: entry.client,
                files: entry.uploadedFiles,
                hours: entry.editedHours || entry.originalHours,
                justification: entry.justification,
                source: "weeklyReport"
              });
            }
          });
        });
      });

      // Buscar todos os jobs do calendário
      const jobsRef = collection(db, "jobs");
      const jobsSnapshot = await getDocs(jobsRef);

      jobsSnapshot.forEach((doc) => {
        const data = doc.data();
        const { clientId, date, uploadedFiles, uid, slug } = data;

        // Só adicionar se tiver arquivos
        if (uploadedFiles && uploadedFiles.length > 0) {
          allDocs.push({
            id: doc.id,
            jobId: doc.id,
            uid: uid,
            slug: slug,
            client: clientsMap[clientId] || clientId, // Resolve client name
            date: date,
            files: uploadedFiles,
            hours: `${data.startTime} - ${data.endTime}`,
            notes: data.notes,
            source: "calendarJob"
          });
        }
      });

      // Ordenar por data mais recente
      allDocs.sort((a, b) => {
        const dateA = new Date(a.weekStart || a.date);
        const dateB = new Date(b.weekStart || b.date);
        return dateB - dateA;
      });

      setDocuments(allDocs);
      setFilteredDocuments(allDocs);
    } catch (err) {
      console.error("Erro ao carregar documentos:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.isAdmin) return;
    fetchAllDocuments();
  }, [user]);

  // Filtrar por cliente
  useEffect(() => {
    if (!searchClient.trim()) {
      setFilteredDocuments(documents);
    } else {
      const filtered = documents.filter((doc) =>
        doc.client.toLowerCase().includes(searchClient.toLowerCase())
      );
      setFilteredDocuments(filtered);
    }
  }, [searchClient, documents]);

  // Agrupar por data
  const groupedByDate = filteredDocuments.reduce((acc, doc) => {
    // Handle both weeklyReport and calendarJob sources
    const dateKey = doc.source === "calendarJob"
      ? doc.date
      : `${doc.weekStart}_${doc.dayName}`;

    if (!acc[dateKey]) {
      acc[dateKey] = {
        weekStart: doc.weekStart,
        dayName: doc.dayName,
        date: doc.date,
        source: doc.source,
        services: []
      };
    }
    acc[dateKey].services.push(doc);
    return acc;
  }, {});

  // Converter para array e agrupar por colaborador
  const dateGroups = Object.values(groupedByDate).map((group) => {
    const byCollaborator = group.services.reduce((acc, service) => {
      // Use slug (exists for both weeklyReport and calendarJob with files)
      // Fallback to "unknown" if slug is missing
      const key = service.slug || "unknown";
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(service);
      return acc;
    }, {});

    return {
      ...group,
      byCollaborator
    };
  });

  // Recarregar documentos (wrapper que seta loading antes de chamar fetchAllDocuments)
  const reloadDocuments = async () => {
    setLoading(true);
    await fetchAllDocuments();
  };

  // Deletar serviço/dia completo
  const deleteService = async (service) => {
    const dateInfo = service.source === "calendarJob"
      ? service.date
      : service.dayName;

    const confirmMessage = language === "en"
      ? `Delete this service for ${service.client} on ${dateInfo}?\n\nThis will remove all ${service.files.length} file(s) and cannot be undone.`
      : `Deletar este serviço de ${service.client} do dia ${dateInfo}?\n\nIsso removerá ${service.files.length} arquivo(s) e não pode ser desfeito.`;

    if (!window.confirm(confirmMessage)) return;

    setDeleting(true);
    try {
      if (service.source === "calendarJob") {
        // Deletar arquivos de job do calendário
        await updateDoc(doc(db, "jobs", service.jobId), {
          uploadedFiles: []
        });
      } else {
        // Deletar arquivos de weeklyReport
        const reportDoc = await getDoc(doc(db, "weeklyReports", service.reportId));
        if (reportDoc.exists()) {
          const data = reportDoc.data();
          const days = { ...data.days };

          if (days[service.dayName] && days[service.dayName][service.index]) {
            days[service.dayName][service.index].uploadedFiles = [];

            await updateDoc(doc(db, "weeklyReports", service.reportId), {
              days: days
            });
          }
        }
      }

      // Recarregar documentos
      await reloadDocuments();
      setSelectedService(null);

      alert(language === "en" ? "Service deleted successfully!" : "Serviço deletado com sucesso!");
    } catch (err) {
      console.error("Erro ao deletar serviço:", err);
      alert(language === "en" ? "Error deleting service. Try again." : "Erro ao deletar serviço. Tente novamente.");
    } finally {
      setDeleting(false);
    }
  };

  // Baixar todos os arquivos como ZIP
  const downloadAllFiles = async (service) => {
    setDownloading(true);
    try {
      const zip = new JSZip();
      const folderName = service.source === "calendarJob"
        ? `${service.client}_${service.date}`
        : `${service.client}_${service.dayName}`;
      const folder = zip.folder(folderName);

      // Baixar cada arquivo
      for (let i = 0; i < service.files.length; i++) {
        const file = service.files[i];
        const url = getImageUrl(file.url);

        try {
          const response = await fetch(url);
          const blob = await response.blob();

          // Extensão baseada no tipo
          const extension = file.type === "image" ? "jpg" : "mp4";
          const fileName = `${i + 1}_${file.name.replace(/\.[^/.]+$/, "")}.${extension}`;

          folder.file(fileName, blob);
        } catch (err) {
          console.error(`Erro ao baixar ${file.name}:`, err);
        }
      }

      // Gerar e baixar o ZIP
      const content = await zip.generateAsync({ type: "blob" });
      const zipFileName = service.source === "calendarJob"
        ? `${service.client}_${service.date}.zip`
        : `${service.client}_${service.dayName}_${service.slug}.zip`;
      saveAs(content, zipFileName);
    } catch (err) {
      console.error("Erro ao criar ZIP:", err);
      alert("Erro ao baixar arquivos. Tente novamente.");
    } finally {
      setDownloading(false);
    }
  };

  // Converter URL do Cloudinary para JPG
  const getImageUrl = (url) => {
    return url.replace('/upload/', '/upload/f_jpg,q_auto/');
  };

  // Toggle document selection
  const toggleDocSelection = (docId, service) => {
    setSelectedDocs(prev => {
      const exists = prev.find(d => d.id === docId);
      if (exists) {
        return prev.filter(d => d.id !== docId);
      } else {
        return [...prev, { id: docId, service }];
      }
    });
  };

  // Send documents via email
  const sendEmail = async () => {
    if (!emailData.clientEmail.trim() || !emailData.clientName.trim()) {
      setEmailError(language === "en" ? "Please fill in client name and email" : "Por favor preencha nome e email do cliente");
      return;
    }

    if (selectedDocs.length === 0) {
      setEmailError(language === "en" ? "Please select at least one document" : "Por favor selecione pelo menos um documento");
      return;
    }

    setSending(true);
    setEmailError("");
    setEmailSuccess("");
    setUploadProgress({ stage: language === "pt" ? "Preparando documentos..." : "Preparing documents...", percent: 10 });

    try {
      // Prepare documents data
      const documentsToSend = selectedDocs.map(doc => {
        const service = doc.service;
        const dateStr = service.source === "calendarJob"
          ? fnsFormat(parseISO(service.date), "EEEE, dd/MM/yyyy", { locale: language === "pt" ? ptBR : enUS })
          : `${service.dayName} - ${fnsFormat(parseISO(service.weekStart), "dd/MM/yyyy")}`;

        return {
          date: dateStr,
          collaboratorName: service.slug || "Unknown",
          files: service.files
        };
      });

      setUploadProgress({ stage: language === "pt" ? "Criando ZIP e enviando email..." : "Creating ZIP and sending email...", percent: 30 });

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev.percent >= 90) return prev;
          return { ...prev, percent: Math.min(prev.percent + 5, 90) };
        });
      }, 2000);

      // Call backend endpoint
      const result = await callXeroFunction('sendDocumentsEmailBackend', {
        clientEmail: emailData.clientEmail.trim(),
        clientName: emailData.clientName.trim(),
        documents: documentsToSend,
        customMessage: emailData.customMessage.trim()
      });

      clearInterval(progressInterval);
      setUploadProgress({ stage: language === "pt" ? "Concluído!" : "Complete!", percent: 100 });

      console.log("Email sent successfully:", result);
      setEmailSuccess(
        language === "en"
          ? `Email sent successfully to ${emailData.clientEmail}! ${result.fileCount} files sent.`
          : `Email enviado com sucesso para ${emailData.clientEmail}! ${result.fileCount} arquivos enviados.`
      );

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowEmailModal(false);
        setSelectedDocs([]);
        setEmailData({ clientEmail: "", clientName: "", customMessage: "" });
        setUploadProgress({ stage: "", percent: 0 });
      }, 2000);
    } catch (error) {
      console.error("Error sending email:", error);
      setEmailError(
        language === "en"
          ? `Failed to send email: ${error.message}`
          : `Erro ao enviar email: ${error.message}`
      );
      setUploadProgress({ stage: "", percent: 0 });
    } finally {
      setSending(false);
    }
  };

  if (!user?.isAdmin) {
    return (
      <Container className="py-5 text-center">
        <h5>{t(language, "accessDenied")}</h5>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="mb-4 d-flex justify-content-between flex-wrap gap-2 align-items-start">
        <div>
          <h2>📸 {t(language, "serviceDocuments")}</h2>
          <p className="text-muted">
            {t(language, "serviceDocumentsSubtitle")}
          </p>
        </div>
        {filteredDocuments.length > 0 && (
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              // Open modal with ALL available documents
              setShowEmailModal(true);
              setSelectedDocs([]);
              setEmailData({ clientEmail: "", clientName: "", customMessage: "" });
              setEmailError("");
              setEmailSuccess("");
            }}
          >
            📧 {language === "en" ? "Send Email" : "Enviar Email"}
          </Button>
        )}
      </div>

      {/* Busca por cliente */}
      <div className="mb-4">
        <InputGroup>
          <InputGroup.Text>🔍</InputGroup.Text>
          <Form.Control
            type="text"
            placeholder={t(language, "searchByClient")}
            value={searchClient}
            onChange={(e) => setSearchClient(e.target.value)}
          />
          {searchClient && (
            <Button variant="outline-secondary" onClick={() => setSearchClient("")}>
              ✕
            </Button>
          )}
        </InputGroup>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-3">{t(language, "loadingDocuments")}</p>
        </div>
      ) : filteredDocuments.length === 0 ? (
        <div className="alert alert-info">
          {searchClient
            ? `${t(language, "noDocumentsForClient")} "${searchClient}"`
            : t(language, "noDocumentsYet")}
        </div>
      ) : (
        <div>
          {dateGroups.map((dateGroup, idx) => {
            // Calcular data real baseado na semana e dia (weeklyReport) ou usar data direta (calendarJob)
            let actualDate;
            if (dateGroup.source === "calendarJob") {
              actualDate = parseISO(dateGroup.date);
            } else {
              const weekDate = parseISO(dateGroup.weekStart);
              const dayIndex = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].indexOf(dateGroup.dayName);
              actualDate = new Date(weekDate);
              actualDate.setDate(actualDate.getDate() + dayIndex);
            }

            const dayName = language === "en"
              ? fnsFormat(actualDate, "EEE", { locale: enUS })  // Mon, Tue, Wed...
              : fnsFormat(actualDate, "EEEE", { locale: ptBR }).replace("-feira", ""); // Segunda, Terça...

            return (
              <Card key={idx} className="mb-4 shadow-sm">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">
                    📅 {dayName} - {fnsFormat(actualDate, "dd/MM/yyyy")}
                  </h5>
                </Card.Header>
                <Card.Body>
                  {Object.entries(dateGroup.byCollaborator).map(([collaborator, services]) => (
                    <div key={collaborator} className="mb-4">
                      <h6 className="text-secondary mb-3">
                        👤 {collaborator === "unknown" ? (language === "en" ? "Unknown User" : "Usuário Desconhecido") : collaborator}
                      </h6>
                      <Row className="g-3">
                        {services.map((service) => (
                          <Col key={service.id} md={4} sm={6} xs={12}>
                            <Card
                              className="h-100 border"
                              style={{ position: "relative" }}
                            >
                              <div
                                style={{ height: "150px", overflow: "hidden", position: "relative", cursor: "pointer" }}
                                onClick={() => setSelectedService(service)}
                              >
                                {service.files[0].type === "image" ? (
                                  <img
                                    src={getImageUrl(service.files[0].url)}
                                    alt={service.client}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover"
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="bg-dark text-white d-flex align-items-center justify-content-center"
                                    style={{ height: "100%" }}
                                  >
                                    <div>🎥</div>
                                  </div>
                                )}
                                <Badge
                                  bg="dark"
                                  style={{
                                    position: "absolute",
                                    top: "8px",
                                    right: "8px"
                                  }}
                                >
                                  {service.files.length} {service.files.length === 1 ? t(language, "file") : t(language, "files")}
                                </Badge>
                              </div>
                              <Card.Body>
                                <div
                                  style={{ cursor: "pointer" }}
                                  onClick={() => setSelectedService(service)}
                                >
                                  <Card.Title style={{ fontSize: "1rem" }}>
                                    🏠 {service.client}
                                  </Card.Title>
                                  <Card.Text style={{ fontSize: "0.9rem" }} className="text-muted">
                                    ⏰ {service.hours}
                                  </Card.Text>
                                </div>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  className="w-100 mt-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteService(service);
                                  }}
                                  disabled={deleting}
                                >
                                  🗑️ {language === "en" ? "Delete" : "Deletar"}
                                </Button>
                              </Card.Body>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal para visualizar galeria completa */}
      {selectedService && (
        <Modal
          show={!!selectedService}
          onHide={() => { setSelectedService(null); setLightboxIndex(null); }}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              🏠 {selectedService.client}{selectedService.slug ? ` - ${selectedService.slug}` : ""}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3 d-flex justify-content-between align-items-start">
              <div>
                <strong>⏰ {t(language, "schedule")}:</strong> {selectedService.hours}
                {(selectedService.justification || selectedService.notes) && (
                  <div className="mt-2">
                    <strong>📝 {t(language, "justification")}:</strong> {selectedService.justification || selectedService.notes}
                  </div>
                )}
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => downloadAllFiles(selectedService)}
                  disabled={downloading || deleting}
                >
                  {downloading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {t(language, "downloading")}
                    </>
                  ) : (
                    <>📦 {t(language, "downloadAll")}</>
                  )}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => deleteService(selectedService)}
                  disabled={downloading || deleting}
                >
                  {deleting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {language === "en" ? "Deleting..." : "Deletando..."}
                    </>
                  ) : (
                    <>🗑️ {language === "en" ? "Delete Service" : "Deletar Serviço"}</>
                  )}
                </Button>
              </div>
            </div>
            <Row className="g-3">
              {selectedService.files.map((file, idx) => (
                <Col key={idx} md={4} sm={6} xs={12}>
                  <Card>
                    <div
                      style={{ cursor: "pointer", position: "relative" }}
                      onClick={() => setLightboxIndex(idx)}
                    >
                      {file.type === "image" ? (
                        <Card.Img
                          variant="top"
                          src={getImageUrl(file.url)}
                          alt={file.name}
                          style={{ height: "200px", objectFit: "cover" }}
                        />
                      ) : (
                        <div
                          className="bg-dark text-white d-flex align-items-center justify-content-center"
                          style={{ height: "200px" }}
                        >
                          <span style={{ fontSize: "48px" }}>🎥</span>
                        </div>
                      )}
                      {/* Overlay com ícone de expandir */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "rgba(0,0,0,0)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,0,0,0.3)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "rgba(0,0,0,0)"}
                      >
                        <span style={{ fontSize: "32px", opacity: 0, transition: "opacity 0.2s" }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                        >
                          🔍
                        </span>
                      </div>
                    </div>
                    <Card.Body>
                      <small className="d-block text-truncate">{file.name}</small>
                      {(() => {
                        // Tentar pegar timestamp do campo uploadedAt ou extrair da URL do Cloudinary
                        let uploadDate = null;
                        let isFromCloudinaryUrl = false;

                        if (file.uploadedAt) {
                          // Foto nova - tem uploadedAt salvo no Firestore
                          uploadDate = new Date(file.uploadedAt);
                        } else if (file.url) {
                          // Foto antiga - extrair version (timestamp) da URL do Cloudinary
                          // Formato: /upload/v1234567890/
                          const versionMatch = file.url.match(/\/upload\/v(\d+)\//);
                          if (versionMatch) {
                            uploadDate = new Date(parseInt(versionMatch[1]) * 1000);
                            isFromCloudinaryUrl = true;
                          }
                        }

                        if (uploadDate && !isNaN(uploadDate.getTime())) {
                          return (
                            <small className="d-block text-muted mt-1">
                              🕐 {fnsFormat(uploadDate, "dd/MM/yyyy HH:mm", { locale: language === "pt" ? ptBR : enUS })}
                              {/* Só mostrar autor se veio do campo uploadedBy E não é de URL antiga */}
                              {!isFromCloudinaryUrl && file.uploadedBy && ` • ${file.uploadedBy}`}
                            </small>
                          );
                        }
                        return null;
                      })()}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="mt-2 w-100"
                        onClick={() => setLightboxIndex(idx)}
                      >
                        🔍 {language === "en" ? "View" : "Visualizar"}
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Modal.Body>
        </Modal>
      )}

      {/* Lightbox para visualizar fotos em tela cheia */}
      {selectedService && lightboxIndex !== null && (
        <Lightbox
          files={selectedService.files}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(newIndex) => setLightboxIndex(newIndex)}
          getImageUrl={getImageUrl}
        />
      )}

      {/* Modal para enviar documentos por email */}
      <Modal
        show={showEmailModal}
        onHide={() => {
          setShowEmailModal(false);
          setSelectedDocs([]);
          setEmailData({ clientEmail: "", clientName: "", customMessage: "" });
          setEmailError("");
          setEmailSuccess("");
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            📧 {language === "en" ? "Send Documents to Client" : "Enviar Documentos ao Cliente"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {emailSuccess && (
            <Alert variant="success" className="mb-3">
              {emailSuccess}
            </Alert>
          )}
          {emailError && (
            <Alert variant="danger" className="mb-3">
              {emailError}
            </Alert>
          )}

          {/* Client Information */}
          <Form className="mb-4">
            <Form.Group className="mb-3" style={{ position: "relative" }}>
              <Form.Label>
                <strong>{language === "en" ? "Client Name" : "Nome do Cliente"}</strong> *
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={language === "en" ? "Enter client name" : "Digite o nome do cliente"}
                value={emailData.clientName}
                onChange={(e) => handleClientNameChange(e.target.value)}
                onFocus={() => {
                  if (emailData.clientName.trim().length >= 2 && clientSuggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                onBlur={() => {
                  // Delay to allow click on suggestion
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                disabled={sending}
                autoComplete="off"
              />
              {/* Autocomplete Dropdown */}
              {showSuggestions && clientSuggestions.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: "white",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                    maxHeight: "200px",
                    overflowY: "auto"
                  }}
                >
                  {clientSuggestions.map((client) => (
                    <div
                      key={client.id}
                      style={{
                        padding: "10px 12px",
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0"
                      }}
                      onMouseDown={() => handleSelectClient(client)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fa"}
                      onMouseLeave={(e) => e.target.style.backgroundColor = "white"}
                    >
                      <div style={{ fontWeight: "500" }}>{client.name}</div>
                      {client.email && (
                        <small style={{ color: "#6c757d" }}>{client.email}</small>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <strong>{language === "en" ? "Client Email" : "Email do Cliente"}</strong> *
              </Form.Label>
              <Form.Control
                type="email"
                placeholder={language === "en" ? "client@example.com" : "cliente@exemplo.com"}
                value={emailData.clientEmail}
                onChange={(e) => setEmailData({ ...emailData, clientEmail: e.target.value })}
                disabled={sending}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <strong>{language === "en" ? "Custom Message (optional)" : "Mensagem Personalizada (opcional)"}</strong>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder={language === "en" ? "Add a personal message..." : "Adicione uma mensagem personalizada..."}
                value={emailData.customMessage}
                onChange={(e) => setEmailData({ ...emailData, customMessage: e.target.value })}
                disabled={sending}
              />
            </Form.Group>
          </Form>

          {/* Album Selection - Grouped by Date */}
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>
                {language === "en" ? "Select Albums to Send:" : "Selecione Álbuns para Enviar:"}
              </strong>
              <div>
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    // Select all
                    const allDocs = [];
                    dateGroups.forEach((dateGroup) => {
                      Object.entries(dateGroup.byCollaborator).forEach(([collaboratorSlug, services]) => {
                        services.forEach(service => {
                          allDocs.push({ id: service.id, service: service });
                        });
                      });
                    });
                    setSelectedDocs(allDocs);
                  }}
                  disabled={sending}
                >
                  {language === "en" ? "Select All" : "Selecionar Todos"}
                </Button>
                |
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => setSelectedDocs([])}
                  disabled={sending}
                >
                  {language === "en" ? "Deselect All" : "Desmarcar Todos"}
                </Button>
              </div>
            </div>
            <div style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "4px", padding: "10px" }}>
              {dateGroups.map((dateGroup, idx) => {
                // Calculate actual date
                let actualDate;
                if (dateGroup.source === "calendarJob") {
                  actualDate = parseISO(dateGroup.date);
                } else {
                  const weekDate = parseISO(dateGroup.weekStart);
                  const dayIndex = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].indexOf(dateGroup.dayName);
                  actualDate = new Date(weekDate);
                  actualDate.setDate(actualDate.getDate() + dayIndex);
                }

                const dayName = language === "en"
                  ? fnsFormat(actualDate, "EEE", { locale: enUS })
                  : fnsFormat(actualDate, "EEEE", { locale: ptBR }).replace("-feira", "");

                return (
                  <div key={idx} className="mb-3">
                    <div className="bg-light p-2 rounded mb-2">
                      <strong>📅 {dayName} - {fnsFormat(actualDate, "dd/MM/yyyy")}</strong>
                    </div>
                    {Object.entries(dateGroup.byCollaborator).map(([collaboratorSlug, services]) => (
                      <div key={collaboratorSlug}>
                        {services.map((service) => {
                          const isSelected = selectedDocs.some(d => d.id === service.id);
                          const totalFiles = service.files.length;

                          return (
                            <div key={service.id} className="d-flex align-items-center mb-2 p-2 border rounded" style={{ marginLeft: "20px" }}>
                              <Form.Check
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleDocSelection(service.id, service)}
                                disabled={sending}
                                className="me-2"
                              />
                              <div className="flex-grow-1">
                                <div><strong>🏠 {service.client}</strong></div>
                                <small className="text-muted">
                                  👤 {service.slug} | {totalFiles} {totalFiles === 1 ? (language === "en" ? "file" : "arquivo") : (language === "en" ? "files" : "arquivos")}
                                </small>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
            <small className="text-muted mt-2 d-block">
              {language === "en"
                ? `${selectedDocs.length} album(s) selected`
                : `${selectedDocs.length} álbum(ns) selecionado(s)`}
            </small>
          </div>

          {/* Progress Bar */}
          {sending && uploadProgress.percent > 0 && (
            <div className="mt-4">
              <div className="d-flex justify-content-between mb-2">
                <small className="text-muted">{uploadProgress.stage}</small>
                <small className="text-muted">{uploadProgress.percent}%</small>
              </div>
              <div className="progress" style={{ height: "8px" }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${uploadProgress.percent}%` }}
                  aria-valuenow={uploadProgress.percent}
                  aria-valuemin="0"
                  aria-valuemax="100"
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowEmailModal(false)}
            disabled={sending}
          >
            {language === "en" ? "Cancel" : "Cancelar"}
          </Button>
          <Button
            variant="primary"
            onClick={sendEmail}
            disabled={sending || selectedDocs.length === 0}
          >
            {sending ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {language === "en" ? "Sending..." : "Enviando..."}
              </>
            ) : (
              <>📧 {language === "en" ? "Send Email" : "Enviar Email"}</>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
